import {useState, useEffect, useCallback, useMemo} from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  QueryDocumentSnapshot,
  DocumentData,
  where,
  Query,
} from "firebase/firestore";
import {firestore} from "../services/firebase/config";

export interface PaginationOptions {
  pageSize?: number;
  orderByField?: string;
  orderDirection?: "asc" | "desc";
}

export interface UsePaginationReturn<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reset: () => void;
  totalLoaded: number;
}

/**
 * Hook for implementing pagination with Firestore collections
 * Supports infinite scrolling and filtering
 */
export function usePagination<T extends DocumentData>(
  collectionName: string,
  options: PaginationOptions = {},
  filters?: any[],
  searchField?: string,
  searchTerm?: string
): UsePaginationReturn<T> {
  const {
    pageSize = 20,
    orderByField = "createdAt",
    orderDirection = "desc",
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  // Create base query
  const createQuery = useCallback(
    (startAfterDoc?: QueryDocumentSnapshot) => {
      let q: Query = collection(firestore, collectionName);

      // Add filters
      if (filters && filters.length > 0) {
        filters.forEach((filter) => {
          if (filter && filter.field && filter.value !== undefined) {
            q = query(
              q,
              where(filter.field, filter.operator || "==", filter.value)
            );
          }
        });
      }

      // Add search filter if provided
      if (searchField && searchTerm && searchTerm.trim()) {
        // For simple text search, we can use >= and < for prefix matching
        const searchValue = searchTerm.trim().toLowerCase();
        q = query(
          q,
          where(searchField, ">=", searchValue),
          where(searchField, "<", searchValue + "\uf8ff")
        );
      }

      // Add ordering
      q = query(q, orderBy(orderByField, orderDirection));

      // Add pagination
      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }

      q = query(q, limit(pageSize));

      return q;
    },
    [
      collectionName,
      filters,
      searchField,
      searchTerm,
      orderByField,
      orderDirection,
      pageSize,
    ]
  );

  // Reset function
  const reset = useCallback(() => {
    setData([]);
    setLastDoc(null);
    setHasMore(true);
    setError(null);
    setInitialLoading(true);
  }, []);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError(null);

    try {
      const q = createQuery(lastDoc || undefined);
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setHasMore(false);
        return;
      }

      const newData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as unknown as T[];

      // Check if we got fewer docs than requested - means we're at the end
      if (newData.length < pageSize) {
        setHasMore(false);
      }

      setData((prev) => (initialLoading ? newData : [...prev, ...newData]));
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
      console.error("Pagination error:", err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [loading, hasMore, lastDoc, createQuery, pageSize, initialLoading]);

  // Reset when filters change
  useEffect(() => {
    reset();
  }, [reset, filters, searchTerm]);

  // Load initial data
  useEffect(() => {
    if (initialLoading) {
      loadMore();
    }
  }, [initialLoading, loadMore]);

  const totalLoaded = useMemo(() => data.length, [data.length]);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    reset,
    totalLoaded,
  };
}

/**
 * Hook for simple in-memory filtering and searching of existing data
 * Use this when you already have all data loaded and want to filter/search client-side
 */
export function useClientFiltering<T>(
  data: T[],
  searchTerm: string,
  searchFields: (keyof T)[],
  additionalFilters?: (item: T) => boolean
): T[] {
  return useMemo(() => {
    let filtered = data;

    // Apply search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((item) =>
        searchFields.some((field) => {
          const value = item[field];
          if (typeof value === "string") {
            return value.toLowerCase().includes(searchLower);
          }
          if (typeof value === "number") {
            return value.toString().includes(searchLower);
          }
          return false;
        })
      );
    }

    // Apply additional filters
    if (additionalFilters) {
      filtered = filtered.filter(additionalFilters);
    }

    return filtered;
  }, [data, searchTerm, searchFields, additionalFilters]);
}
